/**
 * Data-entry validation engine.
 *
 * Pure functions — no store or side-effect imports.
 * Mirrors the ESG pattern: per-inputType required fields, keyed error maps.
 */

export const DISCLOSURE_VALUE_VALIDATIONS = Object.freeze({
  NON_NEGATIVE: "non_negative",
  NON_NEGATIVE_VALUE: "non_negative_value",
  NON_NEGATIVE_INTEGER: "non_negative_integer",
  NO_DECIMAL: "no_decimal",
  MAX_LENGTH_6: "max_length_6",
});

const DISCLOSURE_VALUE_VALIDATION_RULE_SET = new Set(
  Object.values(DISCLOSURE_VALUE_VALIDATIONS),
);

export const REQUIRED_FIELDS = {
  simple: ["label", "reportingMonth", "entryDate"], //value
  simpleLongText: ["label", "reportingMonth", "entryDate"], // "value"
  selector: ["label", "reportingMonth", "entryDate"], // "value"
  value: ["label", "reportingMonth", "entryDate"], // "value"
  valueUnit: ["label", "reportingMonth", "entryDate"], //"value", "unit"
  valueUnitDenominator: ["label", "reportingMonth", "entryDate"], // "value", "unit", "denominator",
  yearValue: ["label", "reportingMonth", "entryDate"], // "year", "value"
  yearValueUnit: ["label", "reportingMonth", "entryDate"], //"year", "value", "unit",
  yearValueCategory: ["label", "reportingMonth", "entryDate"], //"year", "value", "category",
  yearLocationValueUnit: ["label", "reportingMonth", "entryDate"], // "year", "value", "location", "unit",
};

const CONDITIONAL_FIELDS = {
  simple: ["value"],
  simpleLongText: ["value"],
  selector: ["value"],
  value: ["value"],
  valueUnit: ["value", "unit"],
  valueUnitDenominator: ["value", "unit", "denominator"],
  yearValue: ["year", "value"],
  yearValueUnit: ["year", "value", "unit"],
  yearValueCategory: ["year", "value", "category"],
  yearLocationValueUnit: ["year", "value", "location", "unit"],
};

export const FIELD_LABELS = {
  label: "Entry Name",
  entryDate: "Entry Date",
  reportingMonth: "Reporting Date",

  value: "Value",
  unit: "Unit",
  year: "Year",
  location: "Location",
  category: "Category",
  denominator: "Denominator",
  remark: "Remark",
};

export const INPUT_FIELDS_BY_TYPE = {
  simple: ["label", "reportingMonth", "entryDate", "value", "remark"],
  simpleLongText: ["label", "reportingMonth", "entryDate", "value", "remark"],
  selector: ["label", "reportingMonth", "entryDate", "value", "remark"],
  value: ["label", "reportingMonth", "entryDate", "value", "remark"],
  valueUnit: [
    "label",
    "reportingMonth",
    "entryDate",
    "value",
    "unit",
    "remark",
  ],
  valueUnitDenominator: [
    "label",
    "reportingMonth",
    "entryDate",
    "value",
    "unit",
    "denominator",
    "remark",
  ],
  yearValue: [
    "label",
    "reportingMonth",
    "entryDate",
    "year",
    "value",
    "remark",
  ],
  yearValueUnit: [
    "label",
    "reportingMonth",
    "entryDate",
    "year",
    "value",
    "unit",
    "remark",
  ],
  yearValueCategory: [
    "label",
    "reportingMonth",
    "entryDate",
    "year",
    "category",
    "value",
    "remark",
  ],
  yearLocationValueUnit: [
    "label",
    "reportingMonth",
    "entryDate",
    "year",
    "location",
    "value",
    "unit",
    "remark",
  ],
};

export const INPUT_FIELD_WIDTH = {
  label: "1.2fr",
  entryDate: "1fr",
  reportingMonth: "0.9fr",

  year: "0.8fr",
  value: "0.9fr",
  unit: "1fr",
  location: "1fr",
  category: "1fr",
  denominator: "0.9fr",
};

export const getFieldList = (inputType) =>
  INPUT_FIELDS_BY_TYPE[inputType] || INPUT_FIELDS_BY_TYPE.value;

const parseValueValidationNumber = (value) => {
  if (value == null) return null;
  const text = String(value).replace(/,/g, "").trim();
  if (!text) return null;
  const n = Number(text);
  return Number.isFinite(n) ? n : null;
};

const parseValueValidationDigitCount = (value) => {
  if (value == null) return null;
  const text = String(value).replace(/,/g, "").trim();
  if (!text) return null;
  if (!Number.isFinite(Number(text))) return null;

  const digits = text.replace(/^[+-]/, "").replace(/\D/g, "");
  return digits ? digits.length : null;
};

const buildValueValidationContext = (value) => ({
  numericValue: parseValueValidationNumber(value),
  digitCount: parseValueValidationDigitCount(value),
});

const normalizeDisclosureValueValidationRules = (rules = []) => {
  return [
    ...new Set(
      (Array.isArray(rules) ? rules : []).filter((rule) =>
        DISCLOSURE_VALUE_VALIDATION_RULE_SET.has(rule),
      ),
    ),
  ];
};

const resolveDisclosureValueValidationConfig = (options = {}) => ({
  rules: normalizeDisclosureValueValidationRules(options?.validations),
});

const validateNonNegativeInteger = ({ numericValue }) => {
  if (numericValue == null) return null;
  return numericValue < 0 || !Number.isInteger(numericValue)
    ? "The value must be a non-negative integer."
    : null;
};

const validateNoDecimal = ({ numericValue }) => {
  if (numericValue == null) return null;
  return Number.isInteger(numericValue)
    ? null
    : "Decimal values are not allowed.";
};

const validateNonNegative = ({ numericValue }) => {
  if (numericValue == null) return null;
  return numericValue < 0 ? "Negative values are not allowed." : null;
};

const validateNonNegativeValue = ({ numericValue }) => {
  if (numericValue == null) return null;
  return numericValue < 0
    ? "The data value should not be negative value."
    : null;
};

const validateMaxLength6 = ({ digitCount }) => {
  if (digitCount == null) return null;
  return digitCount > 6 ? "The maximum length is 6 digits." : null;
};

const DISCLOSURE_VALUE_VALIDATION_HANDLERS = Object.freeze({
  [DISCLOSURE_VALUE_VALIDATIONS.NON_NEGATIVE_INTEGER]:
    validateNonNegativeInteger,
  [DISCLOSURE_VALUE_VALIDATIONS.NO_DECIMAL]: validateNoDecimal,
  [DISCLOSURE_VALUE_VALIDATIONS.NON_NEGATIVE]: validateNonNegative,
  [DISCLOSURE_VALUE_VALIDATIONS.NON_NEGATIVE_VALUE]: validateNonNegativeValue,
  [DISCLOSURE_VALUE_VALIDATIONS.MAX_LENGTH_6]: validateMaxLength6,
});

const getCombinedValueValidationMessage = (rules = [], context) => {
  for (const rule of rules) {
    const handler = DISCLOSURE_VALUE_VALIDATION_HANDLERS[rule];
    if (!handler) continue;

    const message = handler(context);
    if (message) return message;
  }

  return null;
};

const normalizeValueValidationOptions = (validationOrOptions) =>
  typeof validationOrOptions === "string"
    ? { validations: [validationOrOptions] }
    : validationOrOptions || {};

export const getValueValidationMessage = (value, validationOrOptions) => {
  const config = resolveDisclosureValueValidationConfig(
    normalizeValueValidationOptions(validationOrOptions),
  );
  if (config.rules.length === 0) return null;

  return getCombinedValueValidationMessage(
    config.rules,
    buildValueValidationContext(value),
  );
};

/**
 * Validate a single entry against an inputType.
 * @param {object} entry
 * @param {string} inputType
 * @returns {{ [field]: string }} — field-level error messages (empty if valid)
 */
export function validateEntryFields(entry, inputType, options = {}) {
  const errors = {};
  const isEmpty = (val) => val == null || val === "";

  const fields = REQUIRED_FIELDS[inputType] || ["value"];

  for (const field of fields) {
    if (isEmpty(entry[field])) {
      errors[field] = `${FIELD_LABELS[field] || field} is required`;
    }
  }

  const hasValue = !isEmpty(entry.value);
  const hasRemark = !isEmpty(entry.remark);
  if (hasValue || (!hasValue && !hasRemark)) {
    const conditionalFields = CONDITIONAL_FIELDS[inputType] || [];
    for (const field of conditionalFields) {
      if (isEmpty(entry[field])) {
        errors[field] = `${FIELD_LABELS[field] || field} is required`;
      }
    }
  }

  if (!errors.value && !isEmpty(entry.value)) {
    const validationMessage = getValueValidationMessage(entry.value, options);
    if (validationMessage) {
      errors.value = validationMessage;
    }
  }

  return errors;
}

/**
 * Validate an array of entries against a single inputType.
 * @returns {Map<entryId, { [field]: string }>|null} — null when all valid
 */
export function validateEntries(entries, inputType, options = {}) {
  const errorsMap = new Map();

  for (const entry of entries) {
    const fieldErrors = validateEntryFields(entry, inputType, options);
    if (Object.keys(fieldErrors).length > 0) {
      errorsMap.set(entry.id, fieldErrors);
    }
  }

  return errorsMap.size > 0 ? errorsMap : null;
}

/**
 * Validate sub-entry sections — each section may have its own inputType.
 * @param {Array<{ entries: object[] }>} sections
 * @param {Array<{ inputType: string }>} subEntryDefs
 * @returns {Map<entryId, { [field]: string }>|null} — null when all valid
 */
export function validateSubEntrySections(
  sections,
  subEntryDefs,
  parentOptions = {},
) {
  const errorsMap = new Map();
  const parentConfig = resolveDisclosureValueValidationConfig(parentOptions);

  sections.forEach((section, i) => {
    const inputType = subEntryDefs[i]?.inputType || "value";
    const subEntryOptions = { validations: subEntryDefs[i]?.validations };
    const subEntryConfig =
      resolveDisclosureValueValidationConfig(subEntryOptions);
    const validationOptions =
      subEntryConfig.rules.length > 0 || parentConfig.rules.length === 0
        ? subEntryOptions
        : parentOptions;

    for (const entry of section.entries) {
      const fieldErrors = validateEntryFields(
        entry,
        inputType,
        validationOptions,
      );
      if (Object.keys(fieldErrors).length > 0) {
        errorsMap.set(entry.id, fieldErrors);
      }
    }
  });

  return errorsMap.size > 0 ? errorsMap : null;
}
